import type { RecommendationRow } from './api-client';

const _API_ORIGIN =
  import.meta.env.VITE_VIBECONTROL_API_URL ?? 'https://api.vibecontrol.app';

const API_BASE = _API_ORIGIN + '/api/roleforge';

// ─── Interfaces ──────────────────────────────────────────────────────────────

/**
 * Task 4 (#1738) — identical to `RecommendationRow` (api-client.ts); this was
 * a verbatim hand-copy of that type until the duplication was collapsed.
 * Aliased rather than redeclared so the two can never drift again.
 */
export type AuditTeamRecommendation = RecommendationRow;

export interface AuditTeamResponse {
  recommendations: AuditTeamRecommendation[];
  /** Current audit lifecycle status (e.g. 'ready_to_materialize', 'materialized'). */
  audit_status?: string;
  /** Server flag indicating whether the team can be materialized right now. */
  can_materialize?: boolean;
}

/**
 * REQ-ONE-11 — what a materialization would produce for this organization.
 * The Blueprint's output is a proposal; this is that proposal, and confirming
 * it carries `blueprint_version` back so a Blueprint that moved in the
 * meantime is refused instead of silently applied.
 */
export interface MaterializeProposal {
  // Mirror of the server's `CatalogProposal` (roleforge-catalog-materialize.ts),
  // which the route returns as-is (`res.json(proposal)`) — so every field here
  // is camelCase, exactly as that TS interface serializes. The top-level fields
  // were previously declared snake_case (audit_id, blueprint_version, …), which
  // never matched the wire and silently read as undefined.
  auditId: string;
  organizationId: string;
  workspaceId: string;
  blueprintVersion: string;
  departments: Array<{
    name: string;
    starterKey: string | null;
    origin: 'starter' | 'blueprint';
    description: string | null;
    sourceRoles: string[];
    exists: boolean;
    /** v2 only: a seat marked this department need-to-know. Absent on v1 rows. */
    compartment?: boolean;
  }>;
  skills: Array<{
    name: string;
    skillType: 'role' | 'tool';
    templateId: string | null;
    description: string | null;
    departmentName: string;
    sourceRecommendationId: string | null;
    /** v2 seat grant level. Absent on v1 rows and on tool skills. */
    level?: 'member' | 'manager';
  }>;
  personas: Array<{
    name: string;
    emoji: string | null;
    description: string | null;
    reason: string;
    sourceRecommendationId: string | null;
    /** True when a v2 seat's public_persona named this identity explicitly. */
    fromSeat?: boolean;
  }>;
  /** v2 seats whose captured person has an email. Empty for v1 blueprints. */
  invites: Array<{
    email: string;
    seatName: string;
    roleName: string;
    sourceRecommendationId: string | null;
  }>;
  /** Captured people with a name but no email — "assign later". */
  unassignedPeople: Array<{
    name: string;
    seatName: string;
  }>;
  /**
   * One entry per seat, for the review screen. v1 rows appear with nulls
   * (and the 'member' floor) so the screen still works for v1 audits.
   */
  seats: Array<{
    name: string;
    emoji: string | null;
    departmentName: string;
    compartment: boolean;
    level: 'member' | 'manager';
    humanSummary: string | null;
    aiCharter: string | null;
    personaName: string | null;
    person: { name: string; email: string | null } | null;
    skills: string[];
  }>;
}

/** REQ-ONE-11: what one confirmed materialization actually produced. */
export interface MaterializeCatalogRecord {
  blueprint_version: string;
  applied_at: string;
  org_agent_id: string;
  department_ids: string[];
  created_department_ids: string[];
  role_skill_ids: string[];
  tool_skill_ids: string[];
  persona_agent_ids: string[];
  reapplied: boolean;
}

export interface MaterializeResponse {
  ok?: boolean;
  audit_id?: string;
  /** Legacy/compat field kept for older tests and non-server callers. */
  workspace_id?: string;
  /** URL the client should navigate to after a successful materialize. */
  redirect_url?: string;
  /** Audit lifecycle status after materialize. */
  status?: 'materialized' | 'materializing';
  retry_after_ms?: number;
  /**
   * Internal agent rows created. REQ-ONE-11 removed internal team creation, so
   * this is empty for anything materialized after the pivot; it stays declared
   * because an org that materialized before it still reports its team here.
   */
  provisioned_agent_ids?: string[];
  /** REQ-ONE-11: the department map + catalog + personas this produced. */
  catalog?: MaterializeCatalogRecord;
  operating_model_id?: string;
  handoff_import_id?: string;
}

export type MaterializeStatusResponse =
  | { audit_id: string; status: 'ready_to_materialize' }
  | { audit_id: string; status: 'materializing'; retry_after_ms: number }
  | { audit_id: string; status: 'materialized'; redirect_url?: string }
  /**
   * Issue #1323 — the server already returns `failure_code` and `retriable`
   * (roleforge-materialize.ts `getMaterializeStatus`); the client type simply
   * did not declare them, so the surface could not tell a recoverable failure
   * from a dead end and offered the same "Try again" for both.
   */
  | {
    audit_id: string;
    status: 'failed';
    message: string;
    failure_code?: string | null;
    retriable?: boolean;
  };

// ─── Error class ─────────────────────────────────────────────────────────────

export class MaterializeApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly capKind?: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'MaterializeApiError';
  }
}

// ─── Internal helper ─────────────────────────────────────────────────────────

async function callMaterialize<T>(
  path: string,
  jwt: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as Record<string, string | undefined>;
    throw new MaterializeApiError(
      body.code ?? 'http_error',
      res.status,
      body.cap_kind,
      body.error ?? res.statusText,
    );
  }
  return res.json() as Promise<T>;
}

// ─── Public API functions ─────────────────────────────────────────────────────

/** GET /api/roleforge/audits/:id/team — fetch ordered team recommendations. */
export async function fetchTeam(
  auditId: string,
  jwt: string,
): Promise<AuditTeamResponse> {
  return callMaterialize(`/audits/${auditId}/team`, jwt);
}

/**
 * PATCH /api/roleforge/audits/:id/team — replace team recommendations.
 * The server runs `replace_roleforge_audit_recommendations` and returns the
 * full updated recommendations array.
 */
export async function patchTeam(
  auditId: string,
  recs: Array<Partial<AuditTeamRecommendation> & { position?: number }>,
  jwt: string,
): Promise<AuditTeamResponse> {
  return callMaterialize(`/audits/${auditId}/team`, jwt, {
    method: 'PATCH',
    body: JSON.stringify({ recommendations: recs }),
  });
}

/**
 * GET /api/roleforge/audits/:id/materialize-proposal — REQ-ONE-11.
 * The proposal the owner/admin confirms. Read-only.
 */
export async function fetchMaterializeProposal(
  auditId: string,
  jwt: string,
): Promise<MaterializeProposal> {
  return callMaterialize(`/audits/${auditId}/materialize-proposal`, jwt);
}

/**
 * POST /api/roleforge/audits/:id/materialize — trigger materialize flow.
 *
 * REQ-ONE-11: pass `blueprintVersion` when a proposal was shown first. The
 * server refuses (409 `blueprint_stale`) if the Blueprint changed since, so a
 * proposal the owner reviewed can never be applied as something else. Omitting
 * it means "apply whatever is current" — correct only where the confirmation
 * IS the first look at the output, which is the funnel's activation click.
 *
 * Task 5 (#1738): `opts.sendInvites` rides as `send_invites` — the server only
 * sends pre-staged invitations when it is literally `true`, so it is omitted
 * from the body unless the reviewer opted in.
 */
export async function postMaterialize(
  auditId: string,
  jwt: string,
  blueprintVersion?: string | null,
  opts?: { sendInvites?: boolean },
): Promise<MaterializeResponse> {
  const body: Record<string, unknown> = {};
  if (blueprintVersion) body.blueprint_version = blueprintVersion;
  if (opts?.sendInvites === true) body.send_invites = true;
  return callMaterialize(`/audits/${auditId}/materialize`, jwt, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** GET /api/roleforge/audits/:id/materialize-status — read activation state. */
export async function fetchMaterializeStatus(
  auditId: string,
  jwt: string,
): Promise<MaterializeStatusResponse> {
  return callMaterialize(`/audits/${auditId}/materialize-status`, jwt);
}
